import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { productsApi, productWorkflowsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
    Plus,
    ArrowLeft,
    FlowArrow,
    Package,
    Pencil,
    Trash,
    Copy,
    CheckCircle,
    Play,
    CaretRight
} from '@phosphor-icons/react';

export default function ProductWorkflowManager() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [deleteWorkflow, setDeleteWorkflow] = useState(null);
    const [newWorkflowName, setNewWorkflowName] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [productRes, workflowsRes] = await Promise.all([
                productsApi.getOne(productId),
                productWorkflowsApi.getAll(productId)
            ]);
            setProduct(productRes.data || productRes);
            setWorkflows(workflowsRes.data || workflowsRes);
        } catch (error) {
            toast.error('Errore nel caricamento dei dati');
            navigate('/workflow-console?tab=products');
        } finally {
            setLoading(false);
        }
    }, [productId, navigate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreateWorkflow = async () => {
        if (!newWorkflowName.trim()) {
            toast.error('Inserisci un nome per il workflow');
            return;
        }

        try {
            const response = await productWorkflowsApi.create({
                product_id: productId,
                name: newWorkflowName,
                is_default: isDefault
            });
            const workflow = response.data || response;
            toast.success('Workflow creato con successo');
            setShowCreateDialog(false);
            setNewWorkflowName('');
            setIsDefault(false);
            // Navigate to builder
            navigate(`/workflow-console/product/${productId}/workflow/${workflow.id}`);
        } catch (error) {
            toast.error(error.message || 'Errore nella creazione del workflow');
        }
    };

    const handleDuplicate = async (workflow) => {
        try {
            await productWorkflowsApi.duplicate(workflow.id, {});
            toast.success('Workflow duplicato con successo');
            fetchData();
        } catch (error) {
            toast.error('Errore nella duplicazione del workflow');
        }
    };

    const handleDelete = async () => {
        try {
            await productWorkflowsApi.delete(deleteWorkflow.id);
            toast.success('Workflow eliminato');
            setDeleteWorkflow(null);
            fetchData();
        } catch (error) {
            toast.error('Errore nell\'eliminazione del workflow');
        }
    };

    const handleSetDefault = async (workflow) => {
        try {
            await productWorkflowsApi.update(workflow.id, { is_default: true });
            toast.success('Workflow impostato come predefinito');
            fetchData();
        } catch (error) {
            toast.error('Errore nell\'aggiornamento del workflow');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="product-workflow-manager">
            {/* Back Navigation */}
            <Button 
                variant="ghost" 
                onClick={() => navigate('/workflow-console?tab=products')}
                className="gap-2"
            >
                <ArrowLeft className="w-4 h-4" />
                Torna ai Prodotti
            </Button>

            {/* Product Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                        <Package weight="fill" className="w-7 h-7 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-heading font-bold">{product?.name}</h1>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <code className="text-xs bg-muted px-2 py-0.5 rounded">{product?.code}</code>
                            <span>•</span>
                            <span className="text-sm">{workflows.length} workflow configurati</span>
                        </div>
                    </div>
                </div>
                <Button onClick={() => setShowCreateDialog(true)} data-testid="new-workflow-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Workflow
                </Button>
            </div>

            {/* Workflows List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workflows.map((workflow) => (
                    <div
                        key={workflow.id}
                        className="bg-white border border-border rounded-lg p-5 hover:shadow-md transition-shadow"
                        data-testid={`workflow-card-${workflow.id}`}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FlowArrow weight="duotone" className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-medium">{workflow.name}</h3>
                                    <p className="text-xs text-muted-foreground">
                                        v{workflow.version || 1}
                                    </p>
                                </div>
                            </div>
                            {workflow.is_default && (
                                <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
                                    Default
                                </Badge>
                            )}
                        </div>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                            <span>{workflow.node_count || 0} step</span>
                            <span>•</span>
                            <span>{workflow.edge_count || 0} connessioni</span>
                        </div>

                        <div className="flex gap-2 pt-4 border-t border-border">
                            <Button
                                variant="default"
                                size="sm"
                                className="flex-1"
                                onClick={() => navigate(`/workflow-console/product/${productId}/workflow/${workflow.id}`)}
                                data-testid={`edit-workflow-${workflow.id}`}
                            >
                                <Pencil className="w-4 h-4 mr-1" />
                                Modifica
                            </Button>
                            {!workflow.is_default && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetDefault(workflow)}
                                    title="Imposta come default"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDuplicate(workflow)}
                                title="Duplica"
                            >
                                <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setDeleteWorkflow(workflow)}
                                title="Elimina"
                            >
                                <Trash className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {workflows.length === 0 && (
                <div className="text-center py-16 bg-white border border-border rounded-lg">
                    <FlowArrow className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-medium mb-2">Nessun workflow configurato</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        Crea il primo workflow per questo prodotto. Il workflow definisce la sequenza 
                        di step che verranno eseguiti per ogni contratto associato.
                    </p>
                    <Button onClick={() => setShowCreateDialog(true)} size="lg">
                        <Plus className="w-5 h-5 mr-2" />
                        Crea Workflow
                    </Button>
                </div>
            )}

            {/* Create Workflow Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Nuovo Workflow</DialogTitle>
                        <DialogDescription>
                            Crea un nuovo workflow per il prodotto "{product?.name}"
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="workflow_name">Nome Workflow *</Label>
                            <Input
                                id="workflow_name"
                                value={newWorkflowName}
                                onChange={(e) => setNewWorkflowName(e.target.value)}
                                placeholder="Es: Workflow Standard"
                                data-testid="workflow-name-input"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="is_default"
                                checked={isDefault}
                                onCheckedChange={setIsDefault}
                                data-testid="workflow-default-checkbox"
                            />
                            <Label htmlFor="is_default" className="text-sm">
                                Imposta come workflow predefinito
                            </Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            Annulla
                        </Button>
                        <Button onClick={handleCreateWorkflow} data-testid="create-workflow-submit">
                            <Play className="w-4 h-4 mr-2" />
                            Crea e Configura
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteWorkflow} onOpenChange={() => setDeleteWorkflow(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Elimina Workflow</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sei sicuro di voler eliminare il workflow "{deleteWorkflow?.name}"? 
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
